/**
 * useNotifications Hook
 *
 * Provides real-time notifications state and actions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  notificationService,
  Notification,
  NotificationType,
  NotificationSeverity,
} from '@/services/notificationService';

interface UseNotificationsOptions {
  /** Maximum notifications to fetch (default: 20) */
  limit?: number;
  /** Enable real-time updates (default: true) */
  realtime?: boolean;
}

interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing notifications
 */
export function useNotifications(
  options: UseNotificationsOptions = {}
): UseNotificationsResult {
  const { limit = 20, realtime = true } = options;
  const { activeOrganization, currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!activeOrganization?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [notifs, count] = await Promise.all([
        notificationService.getNotifications(activeOrganization.id, {
          limit,
          currentUserId: uid,
        }),
        notificationService.getUnreadCount(activeOrganization.id, { currentUserId: uid }),
      ]);

      setNotifications(notifs);
      setUnreadCount(count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, limit, uid]);

  // Set up real-time subscription
  useEffect(() => {
    if (!activeOrganization?.id || !realtime) {
      fetchNotifications();
      return;
    }

    setLoading(true);

    // Subscribe to notifications
    const unsubscribeNotifications = notificationService.subscribeToNotifications(
      activeOrganization.id,
      (notifs) => {
        setNotifications(notifs);
        // Derive unread count from the same snapshot so the badge stays in
        // lockstep with the list and reflects per-user read state.
        setUnreadCount(notifs.filter((n) => !n.read).length);
        setLoading(false);
      },
      { limit, currentUserId: uid }
    );

    return () => {
      unsubscribeNotifications();
    };
  }, [activeOrganization?.id, realtime, limit, fetchNotifications, uid]);

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!activeOrganization?.id || !uid) return;

      try {
        await notificationService.markAsRead(activeOrganization.id, notificationId, uid);

        // Optimistically update local state
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, read: true, readBy: [...n.readBy, uid], readAt: new Date() }
              : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Error marking notification as read:', err);
        // Refresh to get correct state
        fetchNotifications();
      }
    },
    [activeOrganization?.id, uid, fetchNotifications]
  );

  // Mark all currently-loaded notifications as read for this user
  const markAllAsRead = useCallback(async () => {
    if (!activeOrganization?.id || !uid) return;

    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (!unreadIds.length) return;

    try {
      await notificationService.markAllAsRead(activeOrganization.id, uid, unreadIds);

      // Optimistically update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.read ? n : { ...n, read: true, readBy: [...n.readBy, uid], readAt: new Date() }
        )
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      // Refresh to get correct state
      fetchNotifications();
    }
  }, [activeOrganization?.id, uid, notifications, fetchNotifications]);

  // Delete notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      if (!activeOrganization?.id) return;

      try {
        await notificationService.deleteNotification(activeOrganization.id, notificationId);

        // Optimistically update local state
        const notification = notifications.find((n) => n.id === notificationId);
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        if (notification && !notification.read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (err) {
        console.error('Error deleting notification:', err);
        // Refresh to get correct state
        fetchNotifications();
      }
    },
    [activeOrganization?.id, notifications, fetchNotifications]
  );

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh: fetchNotifications,
  };
}

/**
 * Get icon and color for notification type
 */
export function getNotificationStyle(type: NotificationType): {
  color: string;
  bgColor: string;
} {
  switch (type) {
    case 'execution_complete':
      return { color: 'text-status-success', bgColor: 'bg-status-success-bg' };
    case 'execution_failed':
      return { color: 'text-accent-red', bgColor: 'bg-accent-red/10' };
    case 'esim_provisioning_failure':
      // Slightly different color so the eSIM icon doesn't blend into the
      // generic execution-failed red — admins glancing at the dropdown can
      // pick out provisioning issues without reading every title.
      return { color: 'text-amber-700', bgColor: 'bg-amber-500/15' };
    case 'schedule_triggered':
      return { color: 'text-accent-blue', bgColor: 'bg-accent-blue/10' };
    case 'device_offline':
      return { color: 'text-accent-red', bgColor: 'bg-accent-red/10' };
    case 'device_online':
      return { color: 'text-status-success', bgColor: 'bg-status-success-bg' };
    case 'system':
    default:
      return { color: 'text-muted-foreground', bgColor: 'bg-muted' };
  }
}

/**
 * Severity → toast type + duration (ms).
 * critical = error/10s, warning = warning/6s, info = info/4s.
 *
 * Icon colors in the bell/page still come from `getNotificationStyle(type)` —
 * keep this severity map toast-only until a surface actually consumes a
 * severity color (and use CSS-variable tokens when one does).
 */
export function getSeverityStyle(severity: NotificationSeverity): {
  toastDuration: number;
  toastType: 'error' | 'warning' | 'info';
} {
  switch (severity) {
    case 'critical':
      return { toastDuration: 10000, toastType: 'error' };
    case 'warning':
      return { toastDuration: 6000, toastType: 'warning' };
    case 'info':
    default:
      return { toastDuration: 4000, toastType: 'info' };
  }
}
